import { v4 as uuidv4 } from "uuid";
import { useCallback, useMemo, } from "react";
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  ConnectionMode,
  Controls,
  MarkerType,
  MiniMap,
  ReactFlow,
  useReactFlow,
  useStore,
  useStoreApi,
} from "@xyflow/react";
import { Box } from "@mui/material";

// components
import CustomNode from "./custom-node";
import { CustomEdge } from "./custom-edge";
import { usePlannerContext } from "../../../services/context/planner-context";
import { axiosInstance } from "../../../services/api/api";
import { useUserContext } from "../../../services/context/user-context";

// style
import "@xyflow/react/dist/style.css";


export const DiagramReactFlow = () => {
  const { nodes, setNodes, edges, setEdges, connectionTypes } =
    usePlannerContext();
    const { viewOnly } = useUserContext();
    const { screenToFlowPosition } = useReactFlow();

    const storeApi = useStoreApi();

    // read from store to see if there's an active click-to-click handle
    const connectionClickStartHandle = useStore((state) => state.connectionClickStartHandle);
  
      // pass store state into each node’s data so the node can highlight the correct handle
  const updatedNodes = useMemo(() => {
    return nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        connectionClickStartHandle,
      },
    }));
  }, [nodes, connectionClickStartHandle]);

    // If user clicks the background (pane), we want to abort the connection

    const onPaneClick = useCallback(() => {
      // If there's a handle selected in "click-to-click" mode,
      // we reset it so no edge is created on the next handle click.
      if (connectionClickStartHandle) {
        storeApi.setState({
          connectionClickStartHandle: null,
        });
      }
    }, [connectionClickStartHandle, storeApi]);
  

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const nodeData = JSON.parse(
        event.dataTransfer.getData("application/reactflow")
      );

      if (!nodeData) {
        return;
      }

      const position = screenToFlowPosition({
        x: event.clientX, 
        y: event.clientY,
      });


      const isolatedDefault = nodeData.node_name === "carparking";
      const maxStoriesDefault = nodeData.node_name === 'office' ? 1 : -1;

      const newNode = {
        id: uuidv4(),
        type: "customNode",
        position,
        deletable: false,
        data: {
          typeId: nodeData.id,
          type: nodeData.node_name,
          typeName: nodeData.display_name,
          color: nodeData.color,
          building: {
            id: null,
            name: "",
            rangeMin: -1,
            rangeMax: -1,
            buildingType: nodeData.id,
            buildingMiroPosition: null,
            height: -1,
            stories: maxStoriesDefault,
            isolated: isolatedDefault,
            cost: -1,
            numEmployees: -1,
            numDocks: -1,
            numParkingSlots: -1,
            prediction: false,
            truckRequirement: -1,
          },
        },
      };

      setNodes((es) => [...es, newNode]);
    },
    [setNodes]
  );

  const onNodesChange = useCallback(
    (changes) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    [setNodes]
  );

  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges]
  );

  const creatConnection = async (connectionData) => {
    try {
      const response = await axiosInstance.post(
        `/connections/create`,
        connectionData
      );

      setEdges((eds) =>
        addEdge(
          {...connectionData.dataStr, id: response.data.connectionId },
          eds
        )
      );
      
    } catch (error) {
      // Handle error
      /* console.log(error, "API error /connections/create"); */
    }
  };

  const onConnect = useCallback(
    (connection) => {
      const handleId =
        /* 'road' */ connection.sourceHandle || connection.targetHandle;
      const handleType = handleId.split("-")[0];
      const connectionType = connectionTypes.find(
        (c) => c.edge_name === handleType
      );

      const sourceBuildingId = nodes.find((n) => n.id === connection.source)
        .data.buildingId;

      const targetBuildingId = nodes.find((n) => n.id === connection.target)
        .data.buildingId;

      const payload = {
        building1: sourceBuildingId,
        building2: targetBuildingId,
        connectionType: connectionType.id,
        dataStr: {
          ...connection,
          type: "customEdge",
          animated: false,
          deletable: false,
          data: {
            label: connectionType.display_name,
            connectionType: handleType,
            typeId: connectionType.id,
            color: connectionType.color,
          },
        },
      };

      creatConnection(payload); 
      if (connectionClickStartHandle) {
        storeApi.setState({
          connectionClickStartHandle: null,
        });
      }
    },
    [nodes]
  );

  const updateBuilding = async (buildingId, buildingInputs) => {
    try {
      const response = await axiosInstance.put(
        `/buildings/updateBuilding/${buildingId}`,
        buildingInputs
      );

    } catch (error) {
      // Handle error
    }
  };

  const onNodeDragStop = async (event, node) => {
    const { id, position } = node;
    if (node && node.data && node.data.building && node.data.buildingId) {
      const buildingInputs = {
        ...node.data.building,
        buildingMiroPosition: {
          id: id,
          type: "customNode",
          position: position,
        },
      };

      updateBuilding(node.data.buildingId, buildingInputs);
    }
  };

  const edgeTypes = {
    customEdge: CustomEdge,
  };

  const isValidConnection = useCallback(
    (connection) => {
      const connectionSource = connection.sourceHandle.split("-")[0];
      const connectionTarget = connection.targetHandle.split("-")[0];
      // Must match handle type
      if (
        connectionSource !== connectionTarget ||
        connection.source === connection.target
      ) {
        return false;
      }
      const type = connectionSource;

      const alreadyExists = edges.some((e) => {
        const edgeType = e.data?.connectionType;

        const isSameType = edgeType === type;
        const connectsTheseTwo =
          (e.source === connection.source && e.target === connection.target) ||
          (e.source === connection.target && e.target === connection.source);

        return isSameType && connectsTheseTwo;
      });

      if (alreadyExists) {
        return false;
      }

      // Otherwise, it's valid
      return true;
    },
    [edges]
  );

  return (
    <Box sx={{ width: "100%", height: "100vh" /* position: 'relative' */ }}>
      <ReactFlow
        nodes={updatedNodes/* nodes */}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={viewOnly ? undefined : onConnect}
        fitView
        style={{ background: "#DBDBEB" }}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onNodeDragStop={onNodeDragStop} // Trigger API update on drag stop
        nodeTypes={{ customNode: CustomNode }}
        edgeTypes={edgeTypes}
        connectionMode={ConnectionMode.Loose}
        /* connectionMode="strict" */
        onPaneClick={onPaneClick}
        isValidConnection={isValidConnection}
        minZoom={0.1}
        connectOnClick={!viewOnly}
        nodesDraggable={!viewOnly}
        nodesConnectable={!viewOnly}
        elementsSelectable={!viewOnly}
      >
        <MiniMap nodeColor={(node) => node.data.color} />
        {/* <Background color='#DBDBEB' gap={16} /> */}
      </ReactFlow>
    </Box>
  );
};
